import "./ApiKeyCell.css";
import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { CheckIcon, CopyIcon, EyeIcon, EyeOffIcon } from "./Icons/Icons";

interface ApiKeyCellProps {
  apiKey?: string | null;
}

/**
 * Renders a plaintext bearer API key masked by default, revealed on click.
 * These list every live key; masking keeps the blast radius down (plan §5).
 * A null/empty key renders as "No key".
 */
const ApiKeyCell: React.FC<ApiKeyCellProps> = ({ apiKey }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  // "Copied" shows briefly, then the button goes back to Copy
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  if (!apiKey) {
    return <span className="api-key-none">No key</span>;
  }

  const masked = `${"•".repeat(8)}${apiKey.slice(-4)}`;

  const handleCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(apiKey).then(() => setCopied(true)).catch(() => {});
    }
  };

  return (
    <span className="api-key">
      <code className="api-key-value">{revealed ? apiKey : masked}</code>
      <Tooltip title={revealed ? "Hide" : "Reveal"} arrow>
        <button type="button" className="api-key-btn" onClick={() => setRevealed((r) => !r)} aria-label={revealed ? "Hide key" : "Reveal key"}>
          {revealed ? <EyeOffIcon/> : <EyeIcon/>}
        </button>
      </Tooltip>
      <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
        <button type="button" className={`api-key-btn ${copied ? "copied" : ""}`} onClick={handleCopy} aria-label="Copy key">
          {copied ? <CheckIcon/> : <CopyIcon/>}
        </button>
      </Tooltip>
    </span>
  );
};

export default ApiKeyCell;
